/**
 * Interface languages: one list for the app, the API and e-mail. English is the source language;
 * a locale without a translation for some text falls back to English for that text.
 */
export const LOCALE_CODES = [
  'en',
  'ru',
  'ja',
  'zh',
  'ko',
  'de',
  'fr',
  'es',
  'it',
  'pt',
  'nl',
  'pl',
  'tr',
  'ar',
  'hi',
  'id',
  'vi',
  'th',
] as const

export type Locale = (typeof LOCALE_CODES)[number]

export const DEFAULT_LOCALE: Locale = 'en'

/** Native name for the language menu, BCP 47 tag for Intl formatting, writing direction. */
export const LOCALE_META: Record<Locale, { name: string; intl: string; rtl?: true }> = {
  en: { name: 'English', intl: 'en-US' },
  ru: { name: 'Русский', intl: 'ru-RU' },
  ja: { name: '日本語', intl: 'ja-JP' },
  zh: { name: '中文', intl: 'zh-CN' },
  ko: { name: '한국어', intl: 'ko-KR' },
  de: { name: 'Deutsch', intl: 'de-DE' },
  fr: { name: 'Français', intl: 'fr-FR' },
  es: { name: 'Español', intl: 'es-ES' },
  it: { name: 'Italiano', intl: 'it-IT' },
  pt: { name: 'Português', intl: 'pt-BR' },
  nl: { name: 'Nederlands', intl: 'nl-NL' },
  pl: { name: 'Polski', intl: 'pl-PL' },
  tr: { name: 'Türkçe', intl: 'tr-TR' },
  ar: { name: 'العربية', intl: 'ar', rtl: true },
  hi: { name: 'हिन्दी', intl: 'hi-IN' },
  id: { name: 'Bahasa Indonesia', intl: 'id-ID' },
  vi: { name: 'Tiếng Việt', intl: 'vi-VN' },
  th: { name: 'ไทย', intl: 'th-TH' },
}

export function isLocale(v: unknown): v is Locale {
  return typeof v === 'string' && (LOCALE_CODES as readonly string[]).includes(v)
}

export function intlLocale(l: Locale): string {
  return LOCALE_META[l].intl
}

/**
 * Best supported locale for an Accept-Language header or a navigator.languages list.
 * Matches on the primary subtag (pt-PT → pt, zh-TW → zh); English when nothing matches.
 */
export function matchLocale(prefs: string | readonly string[] | null | undefined): Locale {
  if (!prefs) return DEFAULT_LOCALE
  const ranked =
    typeof prefs === 'string'
      ? prefs
          .split(',')
          .map((part, i) => {
            const [tag = '', ...params] = part.trim().split(';')
            const q = params.map((p) => /^\s*q=([\d.]+)\s*$/.exec(p)?.[1]).find(Boolean)
            return { tag, q: q === undefined ? 1 : Number(q), i }
          })
          .filter((x) => x.tag && x.q > 0)
          .sort((a, b) => b.q - a.q || a.i - b.i)
          .map((x) => x.tag)
      : prefs
  for (const tag of ranked) {
    const primary = tag.trim().toLowerCase().split(/[-_]/)[0]
    if (isLocale(primary)) return primary
  }
  return DEFAULT_LOCALE
}
